import { createFileRoute } from '@tanstack/react-router';
import { ArrowDown, ArrowUp, ArrowUpDown, Loader2 } from 'lucide-react';
import { Fragment, useMemo, useState } from 'react';

import { Button } from '@apps/web/components/ui/button';
import { Checkbox } from '@apps/web/components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import { Input } from '@apps/web/components/ui/input';
import { Label } from '@apps/web/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@apps/web/components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@apps/web/components/ui/table';
import { Textarea } from '@apps/web/components/ui/textarea';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import { cn } from '@apps/web/lib/utils';
import {
    useCanonicalCaregiverVocab,
    useCanonicalTagRules,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { ApiError, type components } from '@libraries/typescript/api_client';

// FastAPI returns 422 with body { detail: [{ loc: [...], msg, type }, ...] }
// and 400/404/etc with body { detail: "string" }. The default ApiError.message
// is the generic "Request failed with status: NNN" line — that strips the
// per-field validation info, which is the only thing useful when debugging
// admin saves. Pull the real detail off err.body when it's there.
function formatApiError(err: unknown, fallback: string): string {
    if (err instanceof ApiError) {
        const body = err.body as
            | { readonly detail?: unknown }
            | null
            | undefined;
        const detail = body?.detail;
        if (typeof detail === 'string' && detail !== '') {
            return `${fallback}: ${detail}`;
        }
        if (Array.isArray(detail) && detail.length > 0) {
            const items = detail.map((d) => {
                const entry = d as {
                    readonly loc?: ReadonlyArray<string | number>;
                    readonly msg?: string;
                };
                const path = (entry.loc ?? []).join('.');
                const msg = entry.msg ?? 'invalid';
                return path === '' ? msg : `${path}: ${msg}`;
            });
            return `${fallback} (HTTP ${err.status ?? '?'}): ${items.join('; ')}`;
        }
        return err.message
            ? `${fallback}: ${err.message}`
            : `${fallback} (HTTP ${err.status ?? '?'})`;
    }
    if (err instanceof Error && err.message) {
        return `${fallback}: ${err.message}`;
    }
    return fallback;
}

type CanonicalTagRuleRead = components['schemas']['CanonicalTagRuleRead'];
type CheckSpec = CanonicalTagRuleRead['checks'][number];
// CheckSpec read schema (extra="ignore") and CheckSpecUpsert write
// schema (extra="forbid", bounded `value` types) are nominally
// distinct in the generated types even though their fields overlap.
// Save handlers cast read-shape checks into write-shape via this
// alias when posting to the API.
type CheckSpecUpsert = components['schemas']['CheckSpecUpsert'];
export const Route = createFileRoute('/_app/admin/tag-matching')({
    head: () => ({
        meta: [{ title: 'Tag Matching | Phoebe Admin' }],
    }),
    component: CanonicalTagRulesPage,
});

type SortField =
    | 'tag'
    | 'source'
    | 'confidence'
    | 'importance'
    | 'times'
    | 'orgs'
    | 'last_edited';

type ImportanceLevel = 'hard' | 'preferred' | 'soft';
type SortDir = 'asc' | 'desc';

// Confidence ordering is semantic, not alphabetical — high should
// sort above medium and low.
const CONFIDENCE_RANK: Record<string, number> = {
    high: 3,
    medium: 2,
    low: 1,
};

// Visual group signature for one rule. Prefers the durable
// `canonical_concept_key` slug emitted by the LLM (Option 2 — see
// libraries/python/tag_normalization/concept_keys.py). Falls back
// to a hash of (has-set, lacks-set) for legacy rows that haven't
// been re-classified yet, so groups still cluster during the
// rollout window. Returns null when neither signal is present
// (e.g. billing codes with no checks) — those render as singletons.
//
// The "uncategorized" concept_key is treated as null on purpose:
// admin's sentinel for "no concept fits" shouldn't lump every
// uncategorized tag into one giant card.
function computeGroupSignature(rule: CanonicalTagRuleRead): string | null {
    const conceptKey = rule.canonical_concept_key;
    // typeof === 'string' catches null AND undefined in one check.
    // A stale SWR cache from before the column shipped returns rules
    // whose property is missing entirely (undefined), and a strict
    // `!== null` check would treat that as "real concept_key" and
    // produce "concept:undefined" — clustering every stale row into
    // one giant card until SWR revalidates.
    if (
        typeof conceptKey === 'string' &&
        conceptKey !== '' &&
        conceptKey !== 'uncategorized'
    ) {
        return `concept:${conceptKey}`;
    }
    const has = new Set<string>();
    const lacks = new Set<string>();
    for (const check of rule.checks) {
        const tags = Array.isArray(check.tags) ? check.tags : [];
        if (check.type === 'caregiver_has_tag') {
            for (const t of tags) {
                has.add(t);
            }
        } else if (check.type === 'caregiver_lacks_tag') {
            for (const t of tags) {
                lacks.add(t);
            }
        }
    }
    if (has.size === 0 && lacks.size === 0) {
        return null;
    }
    return JSON.stringify({
        has: [...has].sort(),
        lacks: [...lacks].sort(),
    });
}

type GroupRow = {
    readonly key: string;
    readonly primary: CanonicalTagRuleRead;
    readonly members: ReadonlyArray<CanonicalTagRuleRead>;
};

// ClassifierSource values that mean "no human has signed off on this
// rule yet" — the audit queue. Admin Save flips source to
// `phoebe_admin_override`; coordinator org overrides write
// `org_override`. Both drop the row out of the queue automatically.
// Kept at module level so the filter and the count can't diverge if
// new auto-generated sources land in ClassifierSource later.
const NEEDS_REVIEW_SOURCES: ReadonlySet<string> = new Set([
    'llm',
    'heuristic',
    'failed',
    'ambiguous',
]);

// Noise = group has zero caregiver-matching checks across every
// member. If there's nothing to score against, the rule has no
// effect on matching regardless of how the spreadsheet labelled it.
// Stricter than concept_key prefix matching: admin_* groups with
// stray non-empty checks render, env_*/cog_*/etc with no checks
// hide as noise, uncategorized with actual checks renders.
function isNoise(r: GroupRow): boolean {
    return r.members.every((m) => (m.checks ?? []).length === 0);
}

// A group needs review when ANY member still has an auto-generated
// source. `some` not `every` — even one unreviewed member in a
// group means the grouping decision isn't fully audited.
function needsReview(r: GroupRow): boolean {
    return r.members.some((m) => NEEDS_REVIEW_SOURCES.has(m.source));
}

// Collapse sortedRules into display rows, preserving the sort order
// of the first occurrence of each group. Singleton rules (null
// signature, or non-null signature that no other rule shares) emit
// one row with members === [primary].
function groupRulesForDisplay(
    sortedRules: ReadonlyArray<CanonicalTagRuleRead>,
): Array<GroupRow> {
    const membersBySig = new Map<string, Array<CanonicalTagRuleRead>>();
    for (const rule of sortedRules) {
        const sig = computeGroupSignature(rule);
        if (sig === null) {
            continue;
        }
        const list = membersBySig.get(sig) ?? [];
        list.push(rule);
        membersBySig.set(sig, list);
    }
    const out: Array<GroupRow> = [];
    const seen = new Set<string>();
    for (const rule of sortedRules) {
        const sig = computeGroupSignature(rule);
        if (sig === null) {
            out.push({
                key: rule.tag_key,
                primary: rule,
                members: [rule],
            });
            continue;
        }
        if (seen.has(sig)) {
            continue;
        }
        seen.add(sig);
        out.push({
            key: sig,
            primary: rule,
            members: membersBySig.get(sig) ?? [rule],
        });
    }
    return out;
}

// Stricter importance wins when unioning two checks targeting the
// same caregiver-tag set (hard > preferred > soft). Mirrors the
// backend resolver's precedence so the merged rule behaves the same
// way the resolver would when it sees multiple matches.
const IMPORTANCE_RANK: Record<string, number> = {
    hard: 3,
    preferred: 2,
    soft: 1,
};

// Effective importance for a rule: the strictest level across all
// its checks. Returns 'mixed' when checks disagree so the admin
// can see at-a-glance that the rule has heterogenous constraint
// levels (rare, but possible — e.g. one hard caregiver_has_tag and
// one soft caregiver_attribute_equals). Returns null when the rule
// has zero checks (e.g. ignored billing codes).
function getRuleImportance(
    checks: ReadonlyArray<CheckSpec>,
): ImportanceLevel | 'mixed' | null {
    if (checks.length === 0) {
        return null;
    }
    const levels = new Set(checks.map((c) => c.importance));
    if (levels.size > 1) {
        return 'mixed';
    }
    return checks[0]!.importance as ImportanceLevel;
}

// Rewrite every check's importance to the chosen level. Used by
// the inline importance Select on the table row and the dedicated
// Select inside the edit sheet — both write the same shape so the
// behavior is consistent. Attribute checks get the same level as
// caregiver_has/lacks_tag checks; admins who want per-check
// granularity drop down to the raw JSON editor.
function setAllChecksImportance(
    checks: ReadonlyArray<CheckSpec>,
    level: ImportanceLevel,
): Array<CheckSpec> {
    return checks.map((c) => ({ ...c, importance: level }));
}

// Legacy data carries `ambiguous` and `failed` as ClassifierSource
// values — both are classification outcomes, not write actors.
// Surface them as "llm" in the Source column so the column means
// "who wrote this rule"; ambiguity / failure is communicated via
// confidence (low) and explicit admin review.
function formatSourceLabel(source: string): string {
    if (source === 'ambiguous' || source === 'failed') {
        return 'llm';
    }
    return source;
}

function unionChecks(
    current: ReadonlyArray<CheckSpec>,
    incoming: ReadonlyArray<CheckSpec>,
): Array<CheckSpec> {
    const result: Array<CheckSpec> = current.map((c) => ({ ...c }));
    for (const inc of incoming) {
        if (
            inc.type === 'caregiver_has_tag' ||
            inc.type === 'caregiver_lacks_tag'
        ) {
            const matchIdx = result.findIndex((c) => c.type === inc.type);
            const existing = matchIdx !== -1 ? result[matchIdx] : undefined;
            if (existing === undefined) {
                result.push({ ...inc, tags: [...(inc.tags ?? [])] });
                continue;
            }
            const merged = Array.from(
                new Set([
                    ...((existing.tags ?? []) as ReadonlyArray<string>),
                    ...((inc.tags ?? []) as ReadonlyArray<string>),
                ]),
            ).sort();
            const stricterImportance =
                (IMPORTANCE_RANK[inc.importance] ?? 0) >
                (IMPORTANCE_RANK[existing.importance] ?? 0)
                    ? inc.importance
                    : existing.importance;
            result[matchIdx] = {
                ...existing,
                tags: merged,
                importance: stricterImportance,
            };
        } else {
            // attribute_* dedupe must keep the STRICTER importance,
            // matching backend unification.py:union_checks. First-seen
            // wins would silently downgrade hard → soft if iteration
            // order swapped two members with the same (type, field_name,
            // value) but different importance.
            const dupIdx = result.findIndex(
                (c) =>
                    c.type === inc.type &&
                    c.field_name === inc.field_name &&
                    c.value === inc.value,
            );
            if (dupIdx === -1) {
                result.push({ ...inc });
                continue;
            }
            const existing = result[dupIdx]!;
            if (
                (IMPORTANCE_RANK[inc.importance] ?? 0) >
                (IMPORTANCE_RANK[existing.importance] ?? 0)
            ) {
                result[dupIdx] = { ...existing, importance: inc.importance };
            }
        }
    }
    return result;
}

// Module-level so it doesn't remount on every parent state change.
// Mirrors the voice-qa / outreaches / outbox sort-header pattern.
function SortHeader({
    field,
    label,
    align = 'left',
    sortField,
    sortDir,
    onClick,
}: {
    readonly field: SortField;
    readonly label: string;
    readonly align?: 'left' | 'right';
    readonly sortField: SortField;
    readonly sortDir: SortDir;
    readonly onClick: (field: SortField) => void;
}) {
    const isActive = sortField === field;
    const Icon = isActive
        ? sortDir === 'asc'
            ? ArrowUp
            : ArrowDown
        : ArrowUpDown;
    return (
        <Button
            variant="ghost"
            size="sm"
            className={cn(
                '-ml-3 h-6 text-xs',
                align === 'right' && 'ml-auto',
                isActive && 'text-foreground',
            )}
            onClick={() => onClick(field)}
        >
            <Icon className="mr-1 h-3 w-3" />
            {label}
        </Button>
    );
}

function CanonicalTagRulesPage() {
    const { data, isLoading, error, mutate } = useCanonicalTagRules();
    // Group of rules being edited. Single-member for non-grouped
    // tags; multi-member for synonym groups. Setting to null closes
    // the sheet.
    const [editing, setEditing] = useState<{
        readonly members: ReadonlyArray<CanonicalTagRuleRead>;
    } | null>(null);
    // Default: orgs DESC — highest-impact rules first so curation
    // time goes to what matters most.
    const [sortField, setSortField] = useState<SortField>('orgs');
    const [sortDir, setSortDir] = useState<SortDir>('desc');
    // Hide admin/policy/uncategorized groups by default — they have
    // empty checks (no caregiver-matching effect) so they're noise
    // for an admin actively curating real matching rules. Toggle to
    // include them when reviewing the full data set.
    const [showNoise, setShowNoise] = useState(false);
    // Show only rows that still need admin curation. A rule "needs
    // review" iff at least one member's `source` is one of the
    // auto-generated kinds (llm, heuristic, failed, ambiguous) — i.e.
    // no human has Saved/Approved it yet. `phoebe_admin_override` and
    // `org_override` rows are admin-curated and drop out of the queue.
    const [needsReviewOnly, setNeedsReviewOnly] = useState(false);

    // Pass 1: group rules using a stable alpha order so each group's
    // "primary" (the first member that drives the visible raw_tag in
    // the row) doesn't shift when the user re-sorts. `?? []` guards
    // stale SWR cache from pre-PR API responses. Hoisted out of
    // `displayRows` so `needsReviewCount` reuses the same grouped
    // array — keeps the filter and the count exactly in sync.
    const grouped = useMemo(() => {
        if (!data) {
            return [];
        }
        const alphaRules = [...(data.rules ?? [])].sort((a, b) =>
            a.tag_key.localeCompare(b.tag_key),
        );
        return groupRulesForDisplay(alphaRules);
    }, [data]);

    const displayRows = useMemo(() => {
        // Pass 2: order the display rows by the active sort field.
        // - "Orgs" sorts by DISTINCT-org count per concept_key
        //   (backend dedupes the same org carrying multiple synonyms)
        // - "Times" sorts by raw appearance sum across members
        //   (synonyms overlap by design; the count tells you the
        //   raw matching footprint, including duplicates)
        const dir = sortDir === 'asc' ? 1 : -1;
        // Default to {} so stale SWR cache (pre-PR API response that
        // lacked this field) doesn't throw "undefined is not an
        // object." `data` may be undefined while SWR is still
        // loading; the `grouped` memo returns `[]` in that case so
        // the sort below is a no-op.
        const groupPrevalence = data?.group_prevalence_by_concept_key ?? {};
        const groupOrgs = (r: GroupRow): number => {
            const ck = r.primary.canonical_concept_key;
            if (ck && ck !== 'uncategorized' && groupPrevalence[ck]) {
                return groupPrevalence[ck].client_orgs;
            }
            return r.members.reduce((s, m) => s + (m.client_orgs ?? 0), 0);
        };
        const groupTimes = (r: GroupRow): number =>
            r.members.reduce((s, m) => s + (m.client_records ?? 0), 0);
        const visible = grouped.filter((r) => {
            if (!showNoise && isNoise(r)) {
                return false;
            }
            if (needsReviewOnly && !needsReview(r)) {
                return false;
            }
            return true;
        });
        return visible.sort((a, b) => {
            const aNoise = isNoise(a);
            const bNoise = isNoise(b);
            if (aNoise !== bNoise) {
                return aNoise ? 1 : -1;
            }
            if (sortField === 'tag') {
                // Groups first, then singletons; alphabetical within
                // each bucket. ASC keeps the existing default
                // (alphabetical within group / within singleton);
                // DESC reverses the alphabetical order within each
                // bucket but keeps groups above singletons.
                const aGroup = a.members.length > 1;
                const bGroup = b.members.length > 1;
                if (aGroup !== bGroup) {
                    return aGroup ? -1 : 1;
                }
                return a.primary.tag_key.localeCompare(b.primary.tag_key) * dir;
            }
            const cmp = (() => {
                switch (sortField) {
                    case 'source':
                        return a.primary.source.localeCompare(b.primary.source);
                    case 'confidence':
                        return (
                            (CONFIDENCE_RANK[a.primary.confidence] ?? 0) -
                            (CONFIDENCE_RANK[b.primary.confidence] ?? 0)
                        );
                    case 'importance': {
                        const aLevel = getRuleImportance(a.primary.checks);
                        const bLevel = getRuleImportance(b.primary.checks);
                        // 'mixed' and null sort below all named
                        // levels so admins see actionable rows
                        // (real importances) first.
                        const aRank =
                            aLevel === null || aLevel === 'mixed'
                                ? 0
                                : (IMPORTANCE_RANK[aLevel] ?? 0);
                        const bRank =
                            bLevel === null || bLevel === 'mixed'
                                ? 0
                                : (IMPORTANCE_RANK[bLevel] ?? 0);
                        return aRank - bRank;
                    }
                    case 'orgs':
                        return groupOrgs(a) - groupOrgs(b);
                    case 'times':
                        return groupTimes(a) - groupTimes(b);
                    case 'last_edited':
                        return (
                            new Date(a.primary.updated_at).getTime() -
                            new Date(b.primary.updated_at).getTime()
                        );
                    default:
                        return 0;
                }
            })();
            return cmp * dir;
        });
    }, [
        data?.group_prevalence_by_concept_key,
        grouped,
        sortField,
        sortDir,
        showNoise,
        needsReviewOnly,
    ]);

    // Live count for the "Needs review (N)" toggle label. Must match
    // what the user will see when they flip the toggle on, which
    // means honoring `showNoise` — otherwise admins enable the
    // filter, see fewer rows than the label promised, and lose
    // trust in the count.
    const needsReviewCount = useMemo(
        () =>
            grouped.filter((r) => needsReview(r) && (showNoise || !isNoise(r)))
                .length,
        [grouped, showNoise],
    );

    function clickHeader(field: SortField) {
        if (sortField === field) {
            setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            // Numeric/date columns default to DESC (most-impactful first);
            // alphabetical defaults to ASC.
            setSortDir(
                field === 'times' ||
                    field === 'orgs' ||
                    field === 'last_edited' ||
                    field === 'confidence' ||
                    field === 'importance'
                    ? 'desc'
                    : 'asc',
            );
        }
    }

    if (error) {
        return (
            <div className="text-destructive p-6 text-sm">
                Failed to load canonical tag rules:{' '}
                {error instanceof Error ? error.message : 'unknown error'}
            </div>
        );
    }
    if (isLoading || !data) {
        return (
            <div className="flex h-64 items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin" />
            </div>
        );
    }

    return (
        <div className="p-6">
            <div className="mb-6 flex items-center justify-between">
                <div>
                    <h1 className="text-xl font-semibold">Tag Matching</h1>
                    <p className="text-muted-foreground mt-1 text-sm">
                        Phoebe-curated baseline applied to every org. Per-org
                        coordinator overrides win when they exist.
                    </p>
                </div>
                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                        <Checkbox
                            id="needs-review-toggle"
                            checked={needsReviewOnly}
                            onCheckedChange={(c) =>
                                setNeedsReviewOnly(c === true)
                            }
                        />
                        <Label
                            htmlFor="needs-review-toggle"
                            className="cursor-pointer text-sm"
                        >
                            Needs review ({needsReviewCount})
                        </Label>
                    </div>
                    <div className="flex items-center gap-2">
                        <Checkbox
                            id="show-noise-toggle"
                            checked={showNoise}
                            onCheckedChange={(c) => setShowNoise(c === true)}
                        />
                        <Label
                            htmlFor="show-noise-toggle"
                            className="cursor-pointer text-sm"
                        >
                            Show admin / no-op tags
                        </Label>
                    </div>
                </div>
            </div>
            <div className="overflow-x-auto">
                <Table className="min-w-[1200px]">
                    <TableHeader>
                        <TableRow>
                            <TableHead>
                                <SortHeader
                                    field="tag"
                                    label="Tag"
                                    sortField={sortField}
                                    sortDir={sortDir}
                                    onClick={clickHeader}
                                />
                            </TableHead>
                            <TableHead>
                                <SortHeader
                                    field="source"
                                    label="Source"
                                    sortField={sortField}
                                    sortDir={sortDir}
                                    onClick={clickHeader}
                                />
                            </TableHead>
                            <TableHead>
                                <SortHeader
                                    field="confidence"
                                    label="Confidence"
                                    sortField={sortField}
                                    sortDir={sortDir}
                                    onClick={clickHeader}
                                />
                            </TableHead>
                            <TableHead>
                                <SortHeader
                                    field="importance"
                                    label="Importance"
                                    sortField={sortField}
                                    sortDir={sortDir}
                                    onClick={clickHeader}
                                />
                            </TableHead>
                            <TableHead className="text-right">
                                <SortHeader
                                    field="orgs"
                                    label="Orgs"
                                    align="right"
                                    sortField={sortField}
                                    sortDir={sortDir}
                                    onClick={clickHeader}
                                />
                            </TableHead>
                            <TableHead className="text-right">
                                <SortHeader
                                    field="times"
                                    label="Count"
                                    align="right"
                                    sortField={sortField}
                                    sortDir={sortDir}
                                    onClick={clickHeader}
                                />
                            </TableHead>
                            <TableHead>
                                <SortHeader
                                    field="last_edited"
                                    label="Last edited"
                                    sortField={sortField}
                                    sortDir={sortDir}
                                    onClick={clickHeader}
                                />
                            </TableHead>
                            <TableHead className="text-right">
                                Actions
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {displayRows.map((row) => {
                            const isGroup = row.members.length > 1;
                            const primary = row.primary;
                            // "Times" = total appearance count (sum of
                            // per-tag client_records). Synonyms overlap
                            // by design — a client carrying both
                            // Dementia AND Alzheimer's counts twice
                            // because we want to know the raw footprint.
                            // "Orgs" = DISTINCT orgs across the group
                            // members so two synonyms in the same org
                            // don't double-count the org. Singletons /
                            // null-concept rules fall back to per-tag
                            // counts (which are themselves distinct
                            // within their single tag).
                            const conceptKey = primary.canonical_concept_key;
                            const distinctOrgs =
                                conceptKey !== null &&
                                conceptKey !== '' &&
                                conceptKey !== 'uncategorized'
                                    ? data.group_prevalence_by_concept_key?.[
                                          conceptKey
                                      ]?.client_orgs
                                    : undefined;
                            const totalOrgs =
                                distinctOrgs !== undefined
                                    ? distinctOrgs
                                    : row.members.reduce(
                                          (acc, m) =>
                                              acc + (m.client_orgs ?? 0),
                                          0,
                                      );
                            const totalTimes = row.members.reduce(
                                (acc, m) => acc + (m.client_records ?? 0),
                                0,
                            );
                            // Heading prefers the admin-curated display
                            // label; falls back to the primary's EHR
                            // string when no label has been set yet.
                            // When display_label IS set, every member's
                            // raw_tag (including the primary's) shows as
                            // a chip below — the EHR audit trail stays
                            // visible. When display_label is NULL, the
                            // heading is already the primary's raw_tag,
                            // so chips skip the primary to avoid showing
                            // it twice.
                            // typeof === 'string' catches null AND undefined
                            // — same shape guard as computeGroupSignature, so
                            // stale SWR cache (rules missing display_label
                            // entirely) doesn't render `undefined` as the
                            // table heading.
                            const hasDisplayLabel =
                                typeof primary.display_label === 'string' &&
                                primary.display_label !== '';
                            const heading = hasDisplayLabel
                                ? primary.display_label
                                : primary.raw_tag;
                            const chipMembers = hasDisplayLabel
                                ? row.members
                                : row.members.filter(
                                      (m) => m.tag_key !== primary.tag_key,
                                  );
                            return (
                                <TableRow key={row.key}>
                                    <TableCell className="font-medium">
                                        {heading}
                                        {isGroup ? (
                                            <div className="mt-1 flex flex-wrap gap-1">
                                                {chipMembers.map((m) => (
                                                    <span
                                                        key={m.tag_key}
                                                        className="bg-muted text-muted-foreground rounded px-1.5 py-0.5 text-xs font-normal"
                                                    >
                                                        {m.raw_tag}
                                                    </span>
                                                ))}
                                            </div>
                                        ) : hasDisplayLabel ? (
                                            <div className="text-muted-foreground mt-1 text-xs font-normal">
                                                EHR: {primary.raw_tag}
                                            </div>
                                        ) : (
                                            <Fragment />
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {formatSourceLabel(primary.source)}
                                    </TableCell>
                                    <TableCell>{primary.confidence}</TableCell>
                                    <TableCell>
                                        {(() => {
                                            const level = getRuleImportance(
                                                primary.checks,
                                            );
                                            // null = no checks = ignored (no
                                            // caregiver matching).
                                            if (level === null) {
                                                return (
                                                    <span
                                                        className="text-muted-foreground"
                                                        title="No checks — this tag is ignored for matching."
                                                    >
                                                        Ignored
                                                    </span>
                                                );
                                            }
                                            if (level === 'mixed') {
                                                return (
                                                    <span
                                                        className="text-muted-foreground"
                                                        title="Per-check importance varies. Open Edit to homogenize."
                                                    >
                                                        mixed
                                                    </span>
                                                );
                                            }
                                            return level;
                                        })()}
                                    </TableCell>
                                    <TableCell className="text-right text-xs">
                                        {totalOrgs > 0 ? totalOrgs : '—'}
                                    </TableCell>
                                    <TableCell className="text-right text-xs">
                                        {totalTimes > 0
                                            ? totalTimes.toLocaleString()
                                            : '—'}
                                    </TableCell>
                                    <TableCell className="text-xs">
                                        {primary.updated_by_email ?? '—'}
                                        <br />
                                        <span className="text-muted-foreground">
                                            {new Date(
                                                primary.updated_at,
                                            ).toLocaleString()}
                                        </span>
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() =>
                                                setEditing({
                                                    members: row.members,
                                                })
                                            }
                                        >
                                            Edit
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </div>
            {editing !== null ? (
                <EditCanonicalRuleSheet
                    members={editing.members}
                    conceptKeyVocabulary={data.concept_key_vocabulary ?? []}
                    allRules={data.rules ?? []}
                    onClose={() => setEditing(null)}
                    onSaved={() => {
                        setEditing(null);
                        void mutate();
                    }}
                />
            ) : (
                <Fragment />
            )}
        </div>
    );
}

function EditCanonicalRuleSheet({
    members: initialMembers,
    conceptKeyVocabulary,
    allRules,
    onClose,
    onSaved,
}: {
    readonly members: ReadonlyArray<CanonicalTagRuleRead>;
    readonly conceptKeyVocabulary: ReadonlyArray<string>;
    readonly allRules: ReadonlyArray<CanonicalTagRuleRead>;
    readonly onClose: () => void;
    readonly onSaved: () => void;
}) {
    // Fetched here (not in the parent list) so the expensive caregiver
    // vocab scan only runs when the editor actually opens.
    const { data: vocabData } = useCanonicalCaregiverVocab();
    const caregiverVocabulary = useMemo(
        () => vocabData?.caregiver_tag_vocabulary ?? [],
        [vocabData],
    );
    // Local copy of the group's members. Admin can drop members
    // with ✕ — those rules get demoted on Save: their concept_key is
    // cleared, their checks emptied, so they re-render as standalone
    // singletons in the next page load. Admin can then re-classify
    // each one independently. Adding tags via the picker stays the
    // same: their checks get unioned into the editor, and on save
    // the merged rule is written to every current member.
    const [groupMembers, setGroupMembers] =
        useState<ReadonlyArray<CanonicalTagRuleRead>>(initialMembers);
    // Tags the admin removed with ✕ this session. On Save we send
    // one single-rule PUT per dropped tag to clear its concept_key
    // and checks, demoting it to a singleton row. State lives next
    // to groupMembers because dropMember and addToGroup mutate both.
    const [droppedMembers, setDroppedMembers] = useState<
        ReadonlyArray<CanonicalTagRuleRead>
    >([]);
    // The "primary" is always defined: initialMembers is guaranteed
    // non-empty by the page render (a group row always has ≥1
    // member), and dropMember() can't take the count to zero
    // because the group-members chip row only renders while
    // isGroup is true — once the count drops to 1, the chips (and
    // their ✕) disappear.
    const primary: CanonicalTagRuleRead = groupMembers[0] ?? initialMembers[0]!;
    const isGroup = groupMembers.length > 1;
    // raw_tag is no longer editable from the dialog — it's the EHR
    // ground-truth string and only changes via EHR sync. Admins
    // customize the displayed heading via display_label instead.
    const [confidence, setConfidence] = useState(primary.confidence);
    const [conceptKey, setConceptKey] = useState(
        primary.canonical_concept_key ?? '',
    );
    // Admin-curated heading for the group (or for a singleton rule).
    // When set, overrides the table heading + chip primary. When
    // empty, frontend falls back to primary.raw_tag. Bulk-update
    // writes this same value to every member of a group so they all
    // carry identical display_label.
    const [displayLabel, setDisplayLabel] = useState(
        primary.display_label ?? '',
    );
    const [reason, setReason] = useState(primary.reason ?? '');
    // When opening a multi-member group, union EVERY member's checks
    // as the starting state — not just the primary's. Without this,
    // bulk-update on Save would write the primary's checks to all N
    // members, silently dropping the other members' LLM-classified
    // checks. addToGroup() still unions when admin pulls additional
    // tags into the group post-open; this just handles the open-time
    // baseline so admin starts from "everything the group already has".
    const initialMergedChecksJson = useMemo(() => {
        const merged = initialMembers.reduce<Array<CheckSpec>>(
            (acc, member) => unionChecks(acc, member.checks),
            [],
        );
        return JSON.stringify(merged, null, 2);
    }, [initialMembers]);
    const [checksJson, setChecksJson] = useState(initialMergedChecksJson);
    // Snapshot of the dialog's open-time values. We compare the live
    // state against these on Save to decide whether the bulk-update
    // actually has work to do; a Save where the admin only clicked ✕
    // (no field edits, no adds) must NOT fan out a no-op rewrite to
    // every remaining member. See `matcherFieldsDirty` + `handleSave`.
    const initialFieldsSnapshot = useMemo(
        () => ({
            confidence: initialMembers[0]!.confidence,
            conceptKey: initialMembers[0]!.canonical_concept_key ?? '',
            displayLabel: initialMembers[0]!.display_label ?? '',
            reason: initialMembers[0]!.reason ?? '',
            checksJson: initialMergedChecksJson,
        }),
        [initialMembers, initialMergedChecksJson],
    );
    const initialMemberKeys = useMemo(
        () => new Set(initialMembers.map((m) => m.tag_key)),
        [initialMembers],
    );
    const addedMembers = useMemo(
        () => groupMembers.filter((m) => !initialMemberKeys.has(m.tag_key)),
        [groupMembers, initialMemberKeys],
    );
    const matcherFieldsDirty =
        confidence !== initialFieldsSnapshot.confidence ||
        conceptKey !== initialFieldsSnapshot.conceptKey ||
        displayLabel !== initialFieldsSnapshot.displayLabel ||
        reason !== initialFieldsSnapshot.reason ||
        checksJson !== initialFieldsSnapshot.checksJson;
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isApproving, setIsApproving] = useState(false);
    const [isRecompiling, setIsRecompiling] = useState(false);
    const [vocabSearch, setVocabSearch] = useState('');
    const [tagSearch, setTagSearch] = useState('');

    function dropMember(tagKey: string) {
        // Look up the removed member from the current state OUTSIDE
        // the updater, then fire both setters separately. React state
        // updater functions must be pure — calling another setter
        // from inside `setGroupMembers((prev) => ...)` runs twice in
        // StrictMode and misfires on concurrent rerenders.
        const removed = groupMembers.find((m) => m.tag_key === tagKey);
        if (removed !== undefined) {
            setDroppedMembers((d) =>
                d.some((m) => m.tag_key === tagKey) ? d : [...d, removed],
            );
        }
        setGroupMembers((prev) => {
            return prev.filter((m) => m.tag_key !== tagKey);
        });
    }

    // Add another canonical rule to this editor's group as a new
    // member. The added tag JOINS the group's existing checks —
    // it does not contribute its own (see the inner comment for why).
    function addToGroup(target: CanonicalTagRuleRead) {
        // The added tag JOINS this group's existing semantics — it
        // does not contribute its own checks. Two reasons:
        //   1. The tag may be moving here from a huge admin group
        //      (e.g. 558-synonym billing bucket). Unioning would
        //      drag billing/staff tags into a clean matching group
        //      and the destination would start matching caregivers
        //      tagged with garbage.
        //   2. Conceptually, a group's checks are "what caregivers
        //      need to satisfy" for tags in this concept. New
        //      members inherit that contract, not the other way
        //      around. If a new member carries genuinely new
        //      caregiver-tag intent, the admin can manually edit
        //      the checks JSON before saving.
        // We intentionally DO NOT touch checksJson here.
        setGroupMembers((prev) =>
            prev.some((m) => m.tag_key === target.tag_key)
                ? prev
                : [...prev, target],
        );
        // If the admin re-adds a tag they previously dropped this
        // session, cancel the pending demote — the tag stays in the
        // group as if ✕ had never been clicked.
        setDroppedMembers((prev) =>
            prev.filter((m) => m.tag_key !== target.tag_key),
        );
        // Intentionally KEEP the search term so the admin can pick
        // multiple matching tags in one session without re-typing
        // (e.g. searching "smoke" and picking "Smoker", "Smoke Allergy",
        // "Non-Smoker" in sequence). The candidate list automatically
        // hides the just-added tag (groupMembers filter), so the next
        // result is one click away.
    }

    // Other canonical rules eligible to be added to this group.
    // Filters out current members and any tag whose rule has no
    // caregiver_has/lacks targets (those rules are singletons by
    // design — grouping them would be meaningless and would force
    // empty checks into the merged result).
    const candidateTags = useMemo(() => {
        const currentKeys = new Set(groupMembers.map((m) => m.tag_key));
        const q = tagSearch.trim().toLowerCase();
        const eligible = allRules.filter((r) => {
            if (currentKeys.has(r.tag_key)) {
                return false;
            }
            if (computeGroupSignature(r) === null) {
                return false;
            }
            if (q === '') {
                return true;
            }
            return (
                r.tag_key.toLowerCase().includes(q) ||
                r.raw_tag.toLowerCase().includes(q)
            );
        });
        return eligible.slice(0, 100);
    }, [allRules, groupMembers, tagSearch]);

    // Toggle a caregiver tag in the rule's first caregiver_has_tag /
    // caregiver_lacks_tag check, enforcing mutual exclusion with the
    // opposite polarity: a tag cannot be on BOTH has and lacks at
    // the same time, because the rule
    //   caregiver_has_tag=[X] AND caregiver_lacks_tag=[X]
    // is unsatisfiable — no caregiver can both have and lack X.
    // Clicking ✓ on a tag that's currently in lacks moves it to has
    // (and vice-versa); clicking the active polarity again toggles
    // the tag off entirely.
    function toggleVocabTag(tag: string, target: 'has' | 'lacks') {
        const sameType =
            target === 'has' ? 'caregiver_has_tag' : 'caregiver_lacks_tag';
        const oppositeType =
            target === 'has' ? 'caregiver_lacks_tag' : 'caregiver_has_tag';
        let parsed: unknown;
        try {
            parsed = JSON.parse(checksJson);
        } catch {
            return;
        }
        if (!Array.isArray(parsed)) {
            return;
        }
        let next = [...(parsed as Array<Record<string, unknown>>)];

        // First: drop the tag from the opposite polarity check (if
        // present) so polarities can never both fire on the same tag.
        const oppIdx = next.findIndex(
            (c) =>
                typeof c === 'object' &&
                c !== null &&
                c['type'] === oppositeType,
        );
        if (oppIdx !== -1) {
            const oppCheck = { ...next[oppIdx] };
            const oppTags = Array.isArray(oppCheck['tags'])
                ? [...(oppCheck['tags'] as ReadonlyArray<string>)]
                : [];
            const oppAt = oppTags.indexOf(tag);
            if (oppAt !== -1) {
                oppTags.splice(oppAt, 1);
                oppCheck['tags'] = oppTags;
                next[oppIdx] = oppCheck;
                if (oppTags.length === 0) {
                    next = next.filter((_, i) => i !== oppIdx);
                }
            }
        }

        // Then: toggle in/out of the same-polarity check.
        const idx = next.findIndex(
            (c) =>
                typeof c === 'object' && c !== null && c['type'] === sameType,
        );
        if (idx === -1) {
            next.push({
                type: sameType,
                tags: [tag],
                importance: 'soft',
            });
        } else {
            const check = { ...next[idx] };
            const tags = Array.isArray(check['tags'])
                ? [...(check['tags'] as ReadonlyArray<string>)]
                : [];
            const at = tags.indexOf(tag);
            if (at === -1) {
                tags.push(tag);
            } else {
                tags.splice(at, 1);
            }
            check['tags'] = tags;
            next[idx] = check;
            if (tags.length === 0) {
                next = next.filter((_, i) => i !== idx);
            }
        }
        setChecksJson(JSON.stringify(next, null, 2));
    }

    // One memoized parse of checksJson per change. Replaces the
    // scattered JSON.parse-per-call pattern that ran ~5× per dialog
    // render (selectedTags, checksError, Importance Select IIFE,
    // toggleVocabTag, isTagInCheck) — folded into one place that
    // every reader reuses. `parsed` is null when the JSON is
    // malformed; `error` carries the parse failure for the inline
    // textarea hint.
    const parsedChecksState = useMemo<{
        readonly parsed: ReadonlyArray<CheckSpec> | null;
        readonly error: string | null;
    }>(() => {
        try {
            const v = JSON.parse(checksJson);
            if (!Array.isArray(v)) {
                return { parsed: null, error: 'Expected a JSON array' };
            }
            return { parsed: v as ReadonlyArray<CheckSpec>, error: null };
        } catch (err) {
            return {
                parsed: null,
                error: err instanceof Error ? err.message : 'Invalid JSON',
            };
        }
    }, [checksJson]);
    const checksError = parsedChecksState.error;

    // Build BOTH has/lacks sets in one pass off the shared parse, so
    // the caregiver-vocab buttons can color ✓ green / ✗ red in O(1)
    // and the matched-first sort uses set-membership lookups
    // instead of JSON parses per element.
    const selectedSets = useMemo(() => {
        const has = new Set<string>();
        const lacks = new Set<string>();
        if (parsedChecksState.parsed !== null) {
            for (const check of parsedChecksState.parsed) {
                const tags = Array.isArray(check.tags) ? check.tags : [];
                if (check.type === 'caregiver_has_tag') {
                    for (const t of tags) {
                        has.add(t);
                    }
                } else if (check.type === 'caregiver_lacks_tag') {
                    for (const t of tags) {
                        lacks.add(t);
                    }
                }
            }
        }
        return { has, lacks };
    }, [parsedChecksState]);

    function isTagInCheck(tag: string, target: 'has' | 'lacks'): boolean {
        return target === 'has'
            ? selectedSets.has.has(tag)
            : selectedSets.lacks.has(tag);
    }

    // Matched-first ordering: any string currently selected on this
    // rule (either ✓ positive or ✗ negative) floats to the top, then
    // alphabetical.
    const filteredVocab = useMemo(() => {
        const q = vocabSearch.trim().toLowerCase();
        const filtered =
            q === ''
                ? caregiverVocabulary
                : caregiverVocabulary.filter((v) =>
                      v.toLowerCase().includes(q),
                  );
        return [...filtered].sort((a, b) => {
            const aSelected =
                selectedSets.has.has(a) || selectedSets.lacks.has(a);
            const bSelected =
                selectedSets.has.has(b) || selectedSets.lacks.has(b);
            if (aSelected && !bSelected) {
                return -1;
            }
            if (!aSelected && bSelected) {
                return 1;
            }
            return a.localeCompare(b);
        });
    }, [caregiverVocabulary, vocabSearch, selectedSets]);

    const handleSave = async () => {
        if (checksError !== null) {
            return;
        }
        if (groupMembers.length === 0) {
            return;
        }
        setIsSaving(true);
        try {
            // Parse as the write-shape CheckSpecUpsert (the body type
            // for both single-rule PUT and bulk-update). The runtime
            // fields are identical to the read CheckSpec; only the
            // OpenAPI schema name differs.
            const parsedChecks = JSON.parse(
                checksJson,
            ) as Array<CheckSpecUpsert>;
            const reasonValue = reason.trim() === '' ? null : reason;
            const conceptKeyValue =
                conceptKey.trim() === '' ? null : conceptKey.trim();
            const displayLabelValue =
                displayLabel.trim() === '' ? null : displayLabel.trim();

            // Remove any ✕'d members from the group via parallel
            // single-rule PUTs. "Remove from group" is a column-level
            // change only: clear `canonical_concept_key` (so the row
            // exits the cluster) and `display_label` (the shared
            // group heading no longer applies). Every other column
            // — checks, confidence, reason, raw_tag — is preserved
            // from the row itself. The dropped tag stays a valid
            // standalone canonical rule that the recs engine
            // continues to match against, just no longer grouped.
            //
            // Independent of the bulk-update below: a failure on
            // either side doesn't roll back the other.
            if (droppedMembers.length > 0) {
                // Demote via a body-based call: tag_keys travel in the
                // payload, not the URL path, so keys containing "/" (or
                // "#"/"?") — common in the uncategorized billing/junk
                // bucket — don't routing-miss the way PUT /{tag_key} does
                // once the encoded slash is decoded to a path separator.
                await getApiClient().post({
                    url: '/admin/canonical-tag-rules/demote',
                    body: {
                        tag_keys: droppedMembers.map((m) => m.tag_key),
                    },
                });
            }

            // Bulk-update is the heavy operation (N row locks + N
            // row rewrites + a full-table prevalence scan in one
            // txn). It only does meaningful work when the admin
            // actually edited a matcher field OR pulled in new
            // members — in either case the remaining members need
            // rewriting. A Save that only ✕'d members must NOT fan
            // out an identical rewrite to every remaining row.
            const needsBulkUpdate =
                isGroup && (matcherFieldsDirty || addedMembers.length > 0);

            if (needsBulkUpdate) {
                // Multi-member group: write the merged matcher fields
                // to all members atomically via /bulk-update. Each
                // member's raw_tag is preserved server-side; the
                // bulk endpoint's BulkUpdateData schema does NOT
                // accept raw_tag because it's per-member. display_label
                // IS in the bulk payload — it applies uniformly to
                // every member as the shared group heading.
                await getApiClient().post({
                    url: '/admin/canonical-tag-rules/bulk-update',
                    body: {
                        tag_keys: groupMembers.map((m) => m.tag_key),
                        data: {
                            checks: parsedChecks,
                            confidence,
                            reason: reasonValue,
                            canonical_concept_key: conceptKeyValue,
                            display_label: displayLabelValue,
                        },
                    },
                });
            }

            if (isGroup) {
                const removed =
                    droppedMembers.length > 0
                        ? ` · removed ${droppedMembers.length} from group`
                        : '';
                if (needsBulkUpdate) {
                    toast.success(
                        `Saved group of ${groupMembers.length} tags${removed}`,
                        { persistNotification: false },
                    );
                } else if (droppedMembers.length > 0) {
                    toast.success(
                        droppedMembers.length === 1
                            ? `Removed ${droppedMembers[0]!.raw_tag} from group`
                            : `Removed ${droppedMembers.length} tags from group`,
                        { persistNotification: false },
                    );
                } else {
                    toast.success('No changes to save', {
                        persistNotification: false,
                    });
                }
            } else {
                // Single-rule save. raw_tag passes through the
                // primary's original value — the EHR string is the
                // rule's identity and isn't admin-editable. Only the
                // matcher fields + display_label change.
                //
                // droppedMembers can still be non-empty here: if the
                // admin opened a group of 2+ and ✕'d enough chips to
                // reach exactly 1 remaining member, isGroup flips to
                // false but the demote PUTs above already fired for
                // the removed rows. Surface that in the toast so the
                // admin sees their removals were persisted.
                await getApiClient().put({
                    url: '/admin/canonical-tag-rules/{tag_key}',
                    pathParams: { tag_key: primary.tag_key },
                    body: {
                        raw_tag: primary.raw_tag,
                        checks: parsedChecks,
                        confidence,
                        reason: reasonValue,
                        canonical_concept_key: conceptKeyValue,
                        display_label: displayLabelValue,
                    },
                });
                const demoted =
                    droppedMembers.length > 0
                        ? ` · demoted ${droppedMembers.length}`
                        : '';
                toast.success(`Canonical rule saved${demoted}`, {
                    persistNotification: false,
                });
            }
            onSaved();
        } catch (err) {
            toast.error(formatApiError(err, 'Failed to save canonical rule'), {
                persistNotification: false,
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleApprove = async () => {
        // Source flips to PHOEBE_ADMIN_OVERRIDE — confirms the
        // auto-classified rule (LLM / heuristic / failed / ambiguous)
        // without changing content.
        setIsApproving(true);
        try {
            await getApiClient().post({
                url: '/admin/canonical-tag-rules/{tag_key}/approve',
                pathParams: { tag_key: primary.tag_key },
                body: {},
            });
            toast.success('Canonical rule approved', {
                persistNotification: false,
            });
            onSaved();
        } catch (err) {
            toast.error(
                formatApiError(err, 'Failed to approve canonical rule'),
                { persistNotification: false },
            );
        } finally {
            setIsApproving(false);
        }
    };

    const handleRecompile = async () => {
        // Re-run heuristic + LLM on this rule using current vocab + prompt.
        // Updates in place (preserves audit trail). For phoebe_admin_override
        // rules, backend returns 409 — show the user a confirm prompt and
        // retry with ?force=true.
        setIsRecompiling(true);
        try {
            const doRecompile = async (force: boolean) => {
                try {
                    await getApiClient().post({
                        url: '/admin/canonical-tag-rules/{tag_key}/recompile',
                        pathParams: { tag_key: primary.tag_key },
                        queryParams: force ? { force: true } : undefined,
                        body: {},
                    });
                    toast.success(
                        `Recompiled "${primary.raw_tag}" — refreshed from current vocab + prompt`,
                        { persistNotification: false },
                    );
                    onSaved();
                    return true;
                } catch (err) {
                    // The 'admin already curated this rule' 409 is the
                    // only soft-fail path that needs a retry-with-force
                    // round-trip. ApiError.body.detail is a string for
                    // 409; fall back to err.message for other shapes.
                    const apiErr = err instanceof ApiError ? err : null;
                    const detailStr =
                        apiErr !== null &&
                        typeof (apiErr.body as { detail?: unknown })?.detail ===
                            'string'
                            ? (apiErr.body as { detail: string }).detail
                            : err instanceof Error
                              ? err.message
                              : '';
                    if (detailStr.includes('phoebe_admin_override') && !force) {
                        return false;
                    }
                    toast.error(formatApiError(err, 'Failed to recompile'), {
                        persistNotification: false,
                    });
                    return true;
                }
            };
            const ok = await doRecompile(false);
            if (!ok) {
                const confirmed = window.confirm(
                    'This rule is admin-curated (phoebe_admin_override). Recompiling will overwrite the curated checks with a fresh LLM classification. Proceed?',
                );
                if (confirmed) {
                    await doRecompile(true);
                }
            }
        } finally {
            setIsRecompiling(false);
        }
    };

    const handleDelete = async () => {
        // Soft-delete; audit row survives via deleted_at.
        if (
            !window.confirm(
                `Soft-delete canonical rule for "${primary.raw_tag}"? Orgs without their own override will lose the canonical baseline for this tag.`,
            )
        ) {
            return;
        }
        setIsDeleting(true);
        try {
            await getApiClient().delete({
                url: '/admin/canonical-tag-rules/{tag_key}',
                pathParams: { tag_key: primary.tag_key },
            });
            toast.success('Canonical rule deleted', {
                persistNotification: false,
            });
            onSaved();
        } catch (err) {
            toast.error(
                formatApiError(err, 'Failed to delete canonical rule'),
                { persistNotification: false },
            );
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <Dialog open={true} onOpenChange={onClose}>
            <DialogContent className="max-h-[92vh] w-[1400px] max-w-[95vw] overflow-y-auto p-6 sm:max-w-[95vw]">
                <DialogHeader className="pb-2">
                    <div className="flex items-center justify-between gap-3 pr-8">
                        <DialogTitle className="text-base">
                            {isGroup
                                ? `Edit group of ${groupMembers.length} tags`
                                : 'Edit rule'}
                        </DialogTitle>
                        {!isGroup ? (
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={handleRecompile}
                                disabled={
                                    isRecompiling ||
                                    isSaving ||
                                    isDeleting ||
                                    isApproving
                                }
                                title="Re-run LLM with current vocab + prompt. Updates in place."
                            >
                                {isRecompiling ? 'Recompiling…' : 'Recompile'}
                            </Button>
                        ) : (
                            <Fragment />
                        )}
                    </div>
                </DialogHeader>
                <div className="space-y-4">
                    {/* Display label + Concept + Confidence + Importance
                        on one row. Display label is the admin-curated
                        heading shown in the table; applies to every
                        member of a group via bulk-update. The per-rule
                        raw_tag (EHR source string) is read-only — it
                        comes from the EHR sync and is the immutable
                        audit anchor. In single-rule mode we surface it
                        as a small subline so admin can see what the
                        agency typed. */}
                    <div className="grid grid-cols-[2fr_2fr_1fr_1fr] gap-3">
                        <div>
                            <Label htmlFor="display_label">Display label</Label>
                            <Input
                                id="display_label"
                                value={displayLabel}
                                onChange={(e) =>
                                    setDisplayLabel(e.target.value)
                                }
                                placeholder={
                                    isGroup
                                        ? 'Group heading (applies to all members)'
                                        : 'Custom heading (optional; defaults to EHR string)'
                                }
                            />
                            {!isGroup && (
                                <p className="text-muted-foreground mt-1 text-xs">
                                    EHR source:{' '}
                                    <span className="font-mono">
                                        {primary.raw_tag}
                                    </span>
                                </p>
                            )}
                        </div>
                        <div>
                            <Label htmlFor="concept_key">Concept key</Label>
                            <Input
                                id="concept_key"
                                list="concept-keys-options"
                                value={conceptKey}
                                onChange={(e) => setConceptKey(e.target.value)}
                                placeholder="e.g. dementia_care (or type new)"
                            />
                            <datalist id="concept-keys-options">
                                {conceptKeyVocabulary.map((k) => (
                                    <option key={k} value={k} />
                                ))}
                            </datalist>
                        </div>
                        <div>
                            <Label htmlFor="confidence">Confidence</Label>
                            <Select
                                value={confidence}
                                onValueChange={(v) =>
                                    setConfidence(
                                        v as CanonicalTagRuleRead['confidence'],
                                    )
                                }
                            >
                                <SelectTrigger id="confidence">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="high">high</SelectItem>
                                    <SelectItem value="medium">
                                        medium
                                    </SelectItem>
                                    <SelectItem value="low">low</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label htmlFor="importance">Importance</Label>
                            {(() => {
                                const parsedChecks =
                                    parsedChecksState.parsed ?? [];
                                const level = getRuleImportance(parsedChecks);
                                // A genuinely empty checks array = "ignore"
                                // (no matching). Only treat it as ignore when
                                // the JSON actually parsed — a parse error
                                // (parsed === null) must stay blank, not be
                                // confused with intentionally-empty checks.
                                const isIgnored =
                                    parsedChecksState.parsed !== null &&
                                    parsedChecks.length === 0;
                                const selectValue = isIgnored
                                    ? 'ignore'
                                    : level === 'mixed' || level === null
                                      ? ''
                                      : level;
                                return (
                                    <Fragment>
                                        <Select
                                            value={selectValue}
                                            // Once ignored there are no checks
                                            // to apply a level to, so disable
                                            // rather than silently no-op. Recover
                                            // by adding a caregiver tag on the
                                            // right (which recreates a check).
                                            disabled={isIgnored}
                                            onValueChange={(v) => {
                                                if (
                                                    parsedChecksState.parsed ===
                                                    null
                                                ) {
                                                    // Malformed JSON — admin
                                                    // must fix it first.
                                                    return;
                                                }
                                                // "Ignore" clears the checks so
                                                // the rule has zero scoring
                                                // effect (same shape as
                                                // billing/admin tags). Any other
                                                // level rewrites every check.
                                                const next =
                                                    v === 'ignore'
                                                        ? []
                                                        : setAllChecksImportance(
                                                              parsedChecksState.parsed,
                                                              v as ImportanceLevel,
                                                          );
                                                setChecksJson(
                                                    JSON.stringify(
                                                        next,
                                                        null,
                                                        2,
                                                    ),
                                                );
                                            }}
                                        >
                                            <SelectTrigger id="importance">
                                                <SelectValue
                                                    placeholder={
                                                        level === 'mixed'
                                                            ? 'mixed (per-check)'
                                                            : '—'
                                                    }
                                                />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="hard">
                                                    hard
                                                </SelectItem>
                                                <SelectItem value="preferred">
                                                    preferred
                                                </SelectItem>
                                                <SelectItem value="soft">
                                                    soft
                                                </SelectItem>
                                                <SelectItem value="ignore">
                                                    ignore (no matching)
                                                </SelectItem>
                                            </SelectContent>
                                        </Select>
                                        {isIgnored ? (
                                            <p className="text-muted-foreground text-xs">
                                                Add caregiver tags on the right
                                                to set importance.
                                            </p>
                                        ) : (
                                            <Fragment />
                                        )}
                                    </Fragment>
                                );
                            })()}
                        </div>
                    </div>
                    {isGroup ? (
                        <div className="space-y-1.5">
                            <Label>Group members</Label>
                            <p className="text-muted-foreground text-xs">
                                Editing applies to every tag in this group. ✕ on
                                a chip demotes it to a singleton on Save — its
                                concept and checks are cleared so admin can
                                re-classify it independently.
                            </p>
                            <div className="flex flex-wrap gap-1.5 pt-1">
                                {groupMembers.map((m) => (
                                    <span
                                        key={m.tag_key}
                                        className="bg-muted text-foreground inline-flex items-center gap-1.5 rounded px-2.5 py-1 text-sm font-medium"
                                    >
                                        {m.raw_tag}
                                        <button
                                            type="button"
                                            onClick={() =>
                                                dropMember(m.tag_key)
                                            }
                                            className="hover:text-destructive"
                                            title="Demote to singleton on Save"
                                        >
                                            ✕
                                        </button>
                                    </span>
                                ))}
                            </div>
                            {droppedMembers.length > 0 ? (
                                <p className="text-destructive pt-1 text-xs">
                                    {droppedMembers.length} tag
                                    {droppedMembers.length === 1
                                        ? ''
                                        : 's'}{' '}
                                    will be demoted to singletons on Save:{' '}
                                    {droppedMembers
                                        .map((m) => m.raw_tag)
                                        .join(', ')}
                                </p>
                            ) : (
                                <Fragment />
                            )}
                        </div>
                    ) : (
                        <Fragment />
                    )}
                    <div className="grid grid-cols-2 gap-6">
                        {/* Left column: client-tag search + Reason +
                            Checks JSON. Picker results only render
                            while there's a non-empty search; when
                            empty, the space below the search input is
                            used by Reason + Checks. */}
                        <div className="space-y-3">
                            <div>
                                <Label htmlFor="tag_search">
                                    Search client tags to add
                                </Label>
                                <p className="text-muted-foreground text-xs">
                                    Unions the picked tag’s checks into this
                                    rule.
                                </p>
                            </div>
                            <Input
                                id="tag_search"
                                placeholder="Search client tags…"
                                value={tagSearch}
                                onChange={(e) => setTagSearch(e.target.value)}
                            />
                            {tagSearch.trim() !== '' ? (
                                <div className="max-h-[200px] overflow-y-auto rounded border p-2">
                                    {candidateTags.length === 0 ? (
                                        <p className="text-muted-foreground py-2 text-xs">
                                            No eligible client tags match
                                            search.
                                        </p>
                                    ) : (
                                        candidateTags.map((t) => (
                                            <button
                                                key={t.tag_key}
                                                type="button"
                                                onClick={() => addToGroup(t)}
                                                className="hover:bg-muted/50 flex w-full items-center justify-between gap-2 rounded px-2 py-1 text-left text-xs"
                                            >
                                                <span className="font-medium">
                                                    {t.raw_tag}
                                                </span>
                                                <span className="text-muted-foreground">
                                                    {t.checks.length} check
                                                    {t.checks.length === 1
                                                        ? ''
                                                        : 's'}{' '}
                                                    · {t.client_orgs ?? 0} org
                                                    {t.client_orgs === 1
                                                        ? ''
                                                        : 's'}
                                                </span>
                                            </button>
                                        ))
                                    )}
                                </div>
                            ) : (
                                <Fragment />
                            )}
                            <div>
                                <Label htmlFor="reason">Reason</Label>
                                <Textarea
                                    id="reason"
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    placeholder="Optional audit note"
                                    rows={2}
                                />
                            </div>
                            <div>
                                <Label htmlFor="checks">Checks (JSON)</Label>
                                <Textarea
                                    id="checks"
                                    value={checksJson}
                                    onChange={(e) =>
                                        setChecksJson(e.target.value)
                                    }
                                    className="h-[360px] resize-none overflow-y-auto font-mono text-xs"
                                />
                                {checksError !== null ? (
                                    <p className="text-destructive mt-1 text-xs">
                                        {checksError}
                                    </p>
                                ) : (
                                    <Fragment />
                                )}
                            </div>
                        </div>
                        {/* Right: caregiver vocab picker. Admin clicks
                            ✓ to add a caregiver string as a positive
                            match (caregiver_has_tag) or ✗ to add it as
                            a negative match (caregiver_lacks_tag —
                            excludes caregivers with that tag). */}
                        <div className="space-y-2">
                            <div>
                                <Label>
                                    Caregiver tag vocabulary · Total:{' '}
                                    {caregiverVocabulary.length}
                                </Label>
                                <p className="text-muted-foreground text-xs">
                                    ✓ = positive match, ✗ = negative match
                                    (excludes).
                                </p>
                            </div>
                            <Input
                                placeholder="Search caregiver tags…"
                                value={vocabSearch}
                                onChange={(e) => setVocabSearch(e.target.value)}
                            />
                            <div className="h-[360px] overflow-y-auto rounded border p-2">
                                {filteredVocab.length === 0 ? (
                                    <p className="text-muted-foreground py-2 text-xs">
                                        No caregiver tags match search.
                                    </p>
                                ) : (
                                    filteredVocab.map((tag) => {
                                        const inHas = isTagInCheck(tag, 'has');
                                        const inLacks = isTagInCheck(
                                            tag,
                                            'lacks',
                                        );
                                        return (
                                            <div
                                                key={tag}
                                                className="hover:bg-muted/50 flex items-center gap-2 rounded px-1 py-1 text-xs"
                                            >
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        toggleVocabTag(
                                                            tag,
                                                            'has',
                                                        )
                                                    }
                                                    disabled={
                                                        checksError !== null
                                                    }
                                                    title="Toggle as POSITIVE match (caregiver_has_tag)"
                                                    className={cn(
                                                        'w-6 rounded text-center',
                                                        inHas
                                                            ? 'bg-green-600 text-white'
                                                            : 'border',
                                                    )}
                                                >
                                                    ✓
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        toggleVocabTag(
                                                            tag,
                                                            'lacks',
                                                        )
                                                    }
                                                    disabled={
                                                        checksError !== null
                                                    }
                                                    title="Toggle as NEGATIVE match (caregiver_lacks_tag — excludes)"
                                                    className={cn(
                                                        'w-6 rounded text-center',
                                                        inLacks
                                                            ? 'bg-red-600 text-white'
                                                            : 'border',
                                                    )}
                                                >
                                                    ✗
                                                </button>
                                                <span className="flex-1 font-mono">
                                                    {tag}
                                                </span>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="mt-6 flex items-center justify-between border-t pt-4">
                    <Button
                        variant="destructive"
                        onClick={handleDelete}
                        disabled={
                            isDeleting ||
                            isSaving ||
                            isApproving ||
                            isRecompiling ||
                            isGroup
                        }
                        title={
                            isGroup
                                ? 'Soft-delete is per-tag. Open this tag on its own to delete.'
                                : undefined
                        }
                    >
                        {isDeleting ? 'Deleting…' : 'Soft-delete'}
                    </Button>
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={onClose}>
                            Cancel
                        </Button>
                        {!isGroup &&
                            NEEDS_REVIEW_SOURCES.has(primary.source) && (
                                <Button
                                    variant="secondary"
                                    onClick={handleApprove}
                                    disabled={
                                        isApproving ||
                                        isSaving ||
                                        isDeleting ||
                                        isRecompiling
                                    }
                                >
                                    {isApproving
                                        ? 'Approving…'
                                        : 'Approve as-is'}
                                </Button>
                            )}
                        <Button
                            onClick={handleSave}
                            disabled={
                                isSaving ||
                                isDeleting ||
                                isApproving ||
                                isRecompiling ||
                                checksError !== null
                            }
                        >
                            {isSaving ? 'Saving…' : 'Save'}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
